import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaRulerCombined,
} from "react-icons/fa";
import apiRequest from "../services/api";

const UnitManagement = () => {
  const [units, setUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Modal Sheet View Controllers
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create | edit | view
  const [selectedUnit, setSelectedUnit] = useState(null);

  // Standard UoM options
  const unitOptions = [
    "LTR",
    "MTR",
    "101",
    "PCS",
    "ROL",
    "MTS",
    "PKG",
    "SET",
    "KLG",

    "PKT",
    "PACKAGE",
    "BOX",
    "BOTTLE",
    "SACHET",
    "CARTON",
    "TUBE",
    "JAR",
    "LITER",
    "KILOGRAMS",
  ];

  // Form State
  const [formData, setFormData] = useState({
    id: null,
    name: "",
  });

  // ============================================================
  // Fetch Units
  // ============================================================
  const fetchUnits = async () => {
    setIsLoading(true);
    setError("");

    try {
      const resp = await apiRequest("products/units");
      setUnits(resp.data || []);
    } catch (err) {
      setError(
        err?.message || "Failed to synchronize runtime configuration units."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  // ============================================================
  // Open Modal
  // ============================================================
  const openModal = (mode, unit = null) => {
    setModalMode(mode);
    setSelectedUnit(unit);
    setError("");

    if (unit) {
      setFormData({
        id: unit.id,
        name: unit.name || "",
      });
    } else {
      setFormData({
        id: null,
        name: "",
      });
    }

    setIsModalOpen(true);
  };

  // ============================================================
  // Save Unit
  // ============================================================
  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name || formData.name.trim() === "") {
      setError("Unit Name parameter is explicitly required.");
      return;
    }

    if (!unitOptions.includes(formData.name.trim().toUpperCase())) {
      setError("Please select a valid Unit of Measurement.");
      return;
    }

    // Prevent updating to exactly the same value
    if (
      modalMode === "edit" &&
      selectedUnit &&
      formData.name.trim().toLowerCase() === selectedUnit.name.toLowerCase()
    ) {
      setError("Name already exists (matches current record casing).");
      return;
    }

    const endpoint = "products/units";
    const method = modalMode === "create" ? "POST" : "PUT";

    try {
      await apiRequest(endpoint, {
        method,
        body: {
          ...formData,
          name: formData.name.trim().toUpperCase(),
        },
      });

      await fetchUnits();
      setIsModalOpen(false);
    } catch (err) {
      setError(
        err?.message ||
          "Unit service pipeline rejected transaction schema verification."
      );
    }
  };

  // ============================================================
  // Delete Unit
  // ============================================================
  const handleDelete = async (id, name) => {
    if (
      !window.confirm(
        `Are you certain you want to permanently drop the Unit of Measurement "${name}"?`
      )
    ) {
      return;
    }

    try {
      await apiRequest(`products/units/${id}`, {
        method: "DELETE",
      });

      await fetchUnits();
    } catch (err) {
      setError(
        err?.message || "Execution engine blocked unit deletion sequence."
      );
    }
  };

  // ============================================================
  // Close Modal
  // ============================================================
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUnit(null);
    setError("");
  };

  const selectClass =
    "w-full min-h-[44px] bg-slate-50 border border-slate-200 rounded-xl px-3 sm:px-4 py-3 text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed transition appearance-none dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:focus:border-emerald-500/50";

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      <div className="w-full max-w-7xl mx-auto space-y-4 sm:space-y-6 pb-20">
        {/* ======================================================
            HEADER
        ======================================================= */}
        <div className="w-full bg-white border border-slate-200 shadow-sm rounded-2xl p-4 sm:p-5 lg:p-6 dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Title */}
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
                <FaRulerCombined className="text-emerald-500 shrink-0" />
                <span className="truncate">Units of Measurement (UoM)</span>
              </h1>

              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Configure standard stock quantifiers
              </p>
            </div>

            {/* Add Button */}
            <button
              type="button"
              onClick={() => openModal("create")}
              className="
                w-full sm:w-auto
                flex items-center justify-center gap-2
                px-4 py-2.5
                bg-emerald-600 hover:bg-emerald-500
                active:bg-emerald-700
                text-white text-xs font-semibold
                rounded-xl
                transition-all
                shadow-lg shadow-emerald-600/20
                whitespace-nowrap
              "
            >
              <FaPlus />
              <span>New Unit</span>
            </button>
          </div>
        </div>

        {/* ======================================================
            ERROR MESSAGE
        ======================================================= */}
        {error && (
          <div className="w-full p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium break-words dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* ======================================================
            UNIT TABLE
        ======================================================= */}
        <div className="w-full bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[10px] sm:text-[11px] font-bold tracking-wider uppercase dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400">
                  <th className="px-4 sm:px-6 py-3 sm:py-4">
                    Quantifier Value (Name)
                  </th>

                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-right">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-slate-700 text-xs dark:divide-white/5 dark:text-slate-300">
                {/* Loading */}
                {isLoading ? (
                  <tr>
                    <td
                      colSpan="2"
                      className="text-center py-10 text-slate-400 dark:text-slate-500"
                    >
                      Querying UoM configurations...
                    </td>
                  </tr>
                ) : units.length === 0 ? (
                  /* Empty */
                  <tr>
                    <td
                      colSpan="2"
                      className="text-center py-10 px-4 text-slate-400 dark:text-slate-500"
                    >
                      No units of measurement found.
                    </td>
                  </tr>
                ) : (
                  /* Data */
                  units.map((unit) => (
                    <tr
                      key={unit.id}
                      className="hover:bg-slate-50 transition-colors dark:hover:bg-white/[0.02]"
                    >
                      {/* Unit Name */}
                      <td className="px-4 sm:px-6 py-3 sm:py-4">
                        <span
                          className="
                            inline-flex items-center
                            px-2.5 sm:px-3 py-1
                            rounded-md
                            bg-slate-50
                            border border-slate-200
                            text-slate-700
                            tracking-wide
                            text-[11px] sm:text-xs
                            font-semibold
                            dark:bg-slate-950/60
                            dark:border-white/10
                            dark:text-slate-200
                          "
                        >
                          {unit.name}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 sm:px-6 py-3 sm:py-4">
                        <div className="flex justify-end items-center gap-1.5 sm:gap-2">
                          {/* View */}
                          <button
                            type="button"
                            onClick={() => openModal("view", unit)}
                            className="
                              w-8 h-8 sm:w-9 sm:h-9
                              flex items-center justify-center
                              bg-slate-100 hover:bg-slate-200
                              rounded-lg
                              text-slate-600
                              border border-slate-200
                              transition
                              dark:bg-white/5
                              dark:hover:bg-white/10
                              dark:text-slate-300
                              dark:border-white/5
                            "
                            title="View"
                          >
                            <FaEye className="text-xs sm:text-sm" />
                          </button>

                          {/* Edit */}
                          <button
                            type="button"
                            onClick={() => openModal("edit", unit)}
                            className="
                              w-8 h-8 sm:w-9 sm:h-9
                              flex items-center justify-center
                              bg-blue-50 hover:bg-blue-100
                              rounded-lg
                              text-blue-600
                              border border-blue-200
                              transition
                              dark:bg-blue-600/10
                              dark:hover:bg-blue-600/20
                              dark:text-blue-400
                              dark:border-blue-500/10
                            "
                            title="Modify"
                          >
                            <FaEdit className="text-xs sm:text-sm" />
                          </button>

                          {/* Delete */}
                          <button
                            type="button"
                            onClick={() => handleDelete(unit.id, unit.name)}
                            className="
                              w-8 h-8 sm:w-9 sm:h-9
                              flex items-center justify-center
                              bg-red-50 hover:bg-red-100
                              rounded-lg
                              text-red-600
                              border border-red-200
                              transition
                              dark:bg-red-600/10
                              dark:hover:bg-red-600/20
                              dark:text-red-400
                              dark:border-red-500/10
                            "
                            title="Delete"
                          >
                            <FaTrash className="text-xs sm:text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ========================================================
          RESPONSIVE BOTTOM MODAL / DRAWER
      ========================================================= */}
      <div
        className={`
          fixed inset-0 z-[2000]
          flex flex-col justify-end
          transition-opacity duration-300
          ${
            isModalOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }
        `}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/70"
          onClick={closeModal}
        />

        {/* Drawer */}
        <div
          className={`
            relative
            w-full
            max-w-xl
            mx-auto

            bg-white
            border
            border-slate-200

            rounded-t-2xl sm:rounded-t-3xl

            p-4 sm:p-6 lg:p-8

            shadow-2xl

            transition-transform duration-300
            transform

            max-h-[90vh]
            overflow-y-auto

            dark:bg-slate-900
            dark:border-white/10
            dark:shadow-[0_-15px_40px_rgba(0,0,0,0.5)]

            ${isModalOpen ? "translate-y-0" : "translate-y-full"}
          `}
        >
          {/* Modal Header */}
          <div className="flex justify-between items-start gap-4 mb-5 sm:mb-6">
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white capitalize">
                {modalMode} UoM
              </h3>

              <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-1">
                {modalMode === "view"
                  ? "View unit of measurement details."
                  : "Select a standard unit of measurement."}
              </p>
            </div>

            {/* Close */}
            <button
              type="button"
              onClick={closeModal}
              className="
                shrink-0
                w-9 h-9
                flex items-center justify-center
                rounded-xl
                bg-slate-100
                hover:bg-slate-200
                text-slate-600
                border border-slate-200
                transition-colors
                dark:bg-white/5
                dark:hover:bg-white/10
                dark:text-slate-300
                dark:border-white/5
              "
              title="Close"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>

          {/* ====================================================
              FORM
          ===================================================== */}
          <form onSubmit={handleSave} className="space-y-5 sm:space-y-6">
            {/* Unit Name */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] sm:text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 dark:text-slate-400">
                <FaRulerCombined className="text-[10px] text-slate-400 dark:text-slate-500" />
                Unit Name
              </label>

              <select
                required
                disabled={modalMode === "view"}
                value={formData.name}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    name: e.target.value,
                  })
                }
                className={selectClass}
              >
                <option
                  value=""
                  disabled
                  className="bg-white text-slate-800 dark:bg-slate-900 dark:text-white"
                >
                  Select Unit
                </option>

                {unitOptions.map((unit) => (
                  <option
                    key={unit}
                    value={unit}
                    className="bg-white text-slate-800 dark:bg-slate-900 dark:text-white"
                  >
                    {unit}
                  </option>
                ))}
              </select>
            </div>

            {/* ==================================================
                FORM FOOTER
            =================================================== */}
            <div
              className="
                flex flex-col-reverse
                sm:flex-row
                sm:justify-end
                gap-2.5 sm:gap-3

                pt-4

                border-t
                border-slate-200
                dark:border-white/5
              "
            >
              {/* Close */}
              <button
                type="button"
                onClick={closeModal}
                className="
                  w-full sm:w-auto
                  px-4
                  py-2.5

                  rounded-xl

                  bg-slate-100
                  hover:bg-slate-200

                  text-slate-700
                  text-xs
                  font-semibold

                  border border-slate-200
                  transition

                  dark:bg-white/5
                  dark:hover:bg-white/10
                  dark:text-slate-300
                  dark:border-white/5
                "
              >
                Close
              </button>

              {/* Save */}
              {modalMode !== "view" && (
                <button
                  type="submit"
                  className="
                    w-full sm:w-auto
                    px-5
                    py-2.5

                    rounded-xl

                    bg-emerald-600
                    hover:bg-emerald-500

                    text-white
                    text-xs
                    font-semibold

                    shadow-md
                    shadow-emerald-600/10

                    transition
                  "
                >
                  {modalMode === "create" ? "Save Unit" : "Apply Modification"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UnitManagement;
